import { scopedTranslation } from '@utils/I18n'

import { formatDistance, type NearbyEvents } from '@utils/embeds/distance'
import type { Radius } from '@utils/embeds/embedConfig'
import { EmbedButton } from './EmbedButton'
import { EmbedEmptyState } from './EmbedEmptyState'

const t = scopedTranslation('embeds.events.search')

type SearchActionsProps = {
  widerRadius: Radius | null
  prominent: boolean
  onWiden: (radius: Radius) => void
  onShowAll: () => void
}

function SearchActions({ widerRadius, prominent, onWiden, onShowAll }: SearchActionsProps) {
  const showAllIsPrimary = prominent && !widerRadius
  return (
    <>
      {widerRadius && (
        <EmbedButton color={prominent ? 'brand' : 'neutral'} outline={!prominent} onClick={() => onWiden(widerRadius)}>
          {t('search_within', { radius: widerRadius })}
        </EmbedButton>
      )}
      <EmbedButton color={showAllIsPrimary ? 'brand' : 'neutral'} outline={!showAllIsPrimary} onClick={onShowAll}>
        {t('show_all')}
      </EmbedButton>
    </>
  )
}

type NothingNearbyProps = {
  search: NearbyEvents
  locale: string
  onWiden: (radius: Radius) => void
  onShowAll: () => void
}

export function NearestEventNotice({
  search: { place, radius, nearest, widerRadius },
  locale,
  onWiden,
  onShowAll,
}: NothingNearbyProps) {
  if (!nearest) return null
  const city = nearest.event.venue.address?.city
  const distance = formatDistance(nearest.distance, locale)

  return (
    <div className="tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-3 tw:rounded-(--embed-radius,1rem) tw:border tw:border-dashed tw:border-gray-300 tw:bg-white tw:px-4 tw:py-3 tw:text-sm tw:text-gray-600">
      <p>
        {city
          ? t('none_within_nearest_in', { radius, place, city, distance })
          : t('none_within_nearest', { radius, place, distance })}
      </p>
      <div className="tw:flex tw:flex-wrap tw:gap-2">
        <SearchActions widerRadius={widerRadius} prominent={false} onWiden={onWiden} onShowAll={onShowAll} />
      </div>
    </div>
  )
}

type NoEventsNearbyProps = {
  search: NearbyEvents
  headingLevel: 2 | 3
  hasOnlineEvents: boolean
  locale: string
  onWiden: (radius: Radius) => void
  onShowAll: () => void
}

export function NoEventsNearby({
  search: { place, radius, nearest, widerRadius },
  headingLevel,
  hasOnlineEvents,
  locale,
  onWiden,
  onShowAll,
}: NoEventsNearbyProps) {
  const city = nearest?.event.venue.address?.city
  const distance = nearest && formatDistance(nearest.distance, locale)

  return (
    <EmbedEmptyState
      headingLevel={headingLevel}
      icon="location"
      title={t('no_events_within', { radius, place })}
      action={<SearchActions widerRadius={widerRadius} prominent onWiden={onWiden} onShowAll={onShowAll} />}
    >
      {nearest && (
        <p>
          {city
            ? t('nearest_named_in', { name: nearest.event.name, city, distance })
            : t('nearest_named', { name: nearest.event.name, distance })}
        </p>
      )}
      {hasOnlineEvents && <p>{t('online_below')}</p>}
    </EmbedEmptyState>
  )
}
